import { useState } from 'react';
import { CheckCircle, ClipboardText, FileArrowDown } from '@phosphor-icons/react';
import type { SubmissionAttachment } from '@cvs/shared';
import { api, type SubmissionRow } from '../lib/api';
import { useAsync } from '../lib/useAsync';
import { formatRelative } from '../lib/format';
import {
  Avatar,
  Button,
  Card,
  EmptyState,
  ErrorState,
  LoadingState,
  Pill,
  SectionHeading,
} from '../components/ui';

export function StudioGrading() {
  const s = useAsync(() => api.admin.grading(), []);
  const [active, setActive] = useState<SubmissionRow | null>(null);

  if (s.loading) return <LoadingState />;
  if (s.error || !s.data) return <ErrorState message={s.error ?? 'Failed to load'} onRetry={s.reload} />;

  const { queue, graded } = s.data;

  function handleGraded() {
    setActive(null);
    s.reload();
  }

  return (
    <div className="space-y-6">
      <SectionHeading
        eyebrow="Studio"
        title="Grading"
        description="Review lab evidence and return grades with feedback."
      />

      {/* To review */}
      <section aria-labelledby="to-review-heading">
        <div className="mb-3 flex items-center gap-3">
          <h3 id="to-review-heading" className="font-bold text-brand-navy">
            To review
          </h3>
          <Pill tone="amber">{queue.length}</Pill>
        </div>

        {queue.length === 0 ? (
          <EmptyState icon={ClipboardText} title="No submissions awaiting review" description="You're all caught up." />
        ) : (
          <ul className="space-y-3">
            {queue.map((sub) => (
              <li key={sub.id}>
                <Card className="flex flex-col gap-4 p-5 sm:flex-row sm:items-center sm:justify-between">
                  <div className="flex min-w-0 items-center gap-3">
                    <Avatar name={sub.userName} />
                    <div className="min-w-0">
                      <div className="truncate font-semibold text-brand-navy">{sub.userName}</div>
                      <div className="truncate text-sm text-slate-600">{sub.labTitle}</div>
                      <div className="mt-0.5 text-xs text-slate-400">Submitted {formatRelative(sub.submittedAt)}</div>
                    </div>
                  </div>
                  <div className="flex shrink-0 items-center gap-4">
                    <EvidenceList attachments={sub.attachments} submissionId={sub.id} />
                    <Button variant="primary" onClick={() => setActive(sub)}>
                      Grade
                    </Button>
                  </div>
                </Card>
              </li>
            ))}
          </ul>
        )}
      </section>

      {/* Recently graded */}
      <section aria-labelledby="graded-heading">
        <div className="mb-3 flex items-center gap-3">
          <h3 id="graded-heading" className="font-bold text-brand-navy">
            Recently graded
          </h3>
          <Pill tone="green">{graded.length}</Pill>
        </div>

        {graded.length === 0 ? (
          <Card className="p-5 text-sm text-slate-500">No graded submissions yet.</Card>
        ) : (
          <ul className="space-y-3">
            {graded.map((sub) => (
              <li key={sub.id}>
                <Card className="p-5">
                  <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                    <div className="flex min-w-0 items-center gap-3">
                      <Avatar name={sub.userName} />
                      <div className="min-w-0">
                        <div className="truncate font-semibold text-brand-navy">{sub.userName}</div>
                        <div className="truncate text-sm text-slate-600">{sub.labTitle}</div>
                        <div className="mt-0.5 text-xs text-slate-400">Submitted {formatRelative(sub.submittedAt)}</div>
                      </div>
                    </div>
                    <Pill tone="green" className="shrink-0 self-start">
                      <CheckCircle size={14} weight="bold" /> {sub.grade ?? 0}/{sub.labPoints}
                    </Pill>
                  </div>
                  {sub.feedback && (
                    <p className="mt-3 rounded-xl bg-slate-50 p-3 text-sm text-slate-600">{sub.feedback}</p>
                  )}
                </Card>
              </li>
            ))}
          </ul>
        )}
      </section>

      {active && <GradeModal submission={active} onClose={() => setActive(null)} onGraded={handleGraded} />}
    </div>
  );
}

/**
 * Evidence links are opened through a URL fetched at click time.
 *
 * The queue embeds a read SAS when it renders, and those expire an hour later —
 * so an instructor part-way through a grading session would click a link and get
 * an XML AuthenticationFailed page instead of the screenshot. Asking for the URL
 * on the click means it cannot be stale, and lets the SAS be far shorter-lived.
 */
function EvidenceList({
  attachments,
  submissionId,
}: {
  attachments: SubmissionAttachment[];
  submissionId: string;
}) {
  const [busy, setBusy] = useState<number | null>(null);

  const open = async (e: React.MouseEvent, index: number) => {
    e.preventDefault();
    // Opened synchronously, before any await: a window.open that happens after
    // one is treated as unrequested and blocked.
    //
    // No 'noopener' in the features here. With it the call returns null by spec
    // even though the tab opens fine, and the branch below then read that as a
    // blocked popup and sent the *current* tab to the file — so evidence never
    // opened in a new page and the instructor lost their place in the queue.
    // The opener is severed by hand instead, while the new tab is still blank
    // and same-origin.
    const w = window.open('', '_blank');
    if (w) {
      try {
        w.opener = null;
      } catch {
        /* not settable in every browser; the tab is ours and about:blank either way */
      }
    }
    setBusy(index);
    try {
      const { url } = await api.gradingAttachmentUrl(submissionId, index);
      if (w) w.location.href = url;
      else window.location.href = url; // popups genuinely blocked — fall back to this tab
    } catch {
      w?.close();
      alert('Could not open that file. Reload the page and try again.');
    } finally {
      setBusy(null);
    }
  };

  if (attachments.length === 0) return <span className="text-sm text-slate-400">No files</span>;
  return (
    <div className="flex flex-col items-end gap-1.5">
      {attachments.map((a, i) => {
        const mock = a.url.startsWith('mock://');
        return (
          <div key={i} className="flex items-center gap-2">
            {a.tag && (
              <span className="rounded-full bg-slate-100 px-2 py-0.5 text-xs font-medium text-slate-500">{a.tag}</span>
            )}
            {mock ? (
              <span className="inline-flex items-center gap-1.5 text-sm font-medium text-slate-400" title="Mock evidence (no live file)">
                <FileArrowDown size={15} /> <span className="max-w-[180px] truncate">{a.name || 'Evidence (mock)'}</span>
              </span>
            ) : (
              <a
                href={a.url}
                onClick={(e) => open(e, i)}
                target="_blank"
                rel="noreferrer"
                title={a.name}
                aria-busy={busy === i}
                className="inline-flex items-center gap-1.5 text-sm font-semibold text-brand-teal hover:underline"
              >
                <FileArrowDown size={15} weight="bold" />{' '}
                <span className="max-w-[180px] truncate">
                  {busy === i ? 'Opening…' : a.name || 'View evidence'}
                </span>
              </a>
            )}
          </div>
        );
      })}
    </div>
  );
}

function GradeModal({
  submission,
  onClose,
  onGraded,
}: {
  submission: SubmissionRow;
  onClose: () => void;
  onGraded: () => void;
}) {
  const max = submission.labPoints;
  const [gradeText, setGradeText] = useState(submission.grade != null ? String(submission.grade) : '');
  const [feedback, setFeedback] = useState(submission.feedback ?? '');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const grade = Number(gradeText);
  const valid = gradeText.trim() !== '' && Number.isFinite(grade) && grade >= 0 && grade <= max;

  async function submit() {
    if (!valid) {
      setError(`Enter a grade between 0 and ${max}.`);
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      await api.admin.grade(submission.id, grade, feedback.trim());
      onGraded();
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Failed to submit grade');
      setSubmitting(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-brand-navy/40 backdrop-blur-sm" onClick={onClose} aria-hidden="true" />
      <Card className="relative z-10 w-full max-w-lg p-6">
        <div className="mb-4">
          <h3 className="text-lg font-bold text-brand-navy">Grade submission</h3>
          <p className="mt-1 flex items-center gap-2 text-sm text-slate-500">
            <span className="font-medium text-slate-700">{submission.userName}</span>
            <span aria-hidden="true">·</span>
            <span>{submission.labTitle}</span>
          </p>
        </div>

        <div className="mb-3">
          <EvidenceList attachments={submission.attachments} submissionId={submission.id} />
        </div>

        <form
          onSubmit={(e) => {
            e.preventDefault();
            void submit();
          }}
          className="space-y-4"
        >
          <div>
            <label htmlFor="grade-input" className="label-mono mb-1 block">
              Grade (0–{max})
            </label>
            <div className="flex items-center gap-2">
              <input
                id="grade-input"
                type="number"
                min={0}
                max={max}
                step={1}
                value={gradeText}
                onChange={(e) => setGradeText(e.target.value)}
                className="w-28 rounded-xl border border-slate-300 px-3 py-2 text-sm font-semibold text-brand-navy focus:border-brand-teal focus:outline-none focus:ring-2 focus:ring-brand-teal/30"
                aria-describedby="grade-help"
                autoFocus
              />
              <span id="grade-help" className="text-sm text-slate-500">
                out of {max} points
              </span>
            </div>
          </div>

          <div>
            <label htmlFor="feedback-input" className="label-mono mb-1 block">
              Feedback
            </label>
            <textarea
              id="feedback-input"
              rows={4}
              value={feedback}
              onChange={(e) => setFeedback(e.target.value)}
              placeholder="What went well, what to improve…"
              className="w-full resize-y rounded-xl border border-slate-300 px-3 py-2 text-sm text-slate-700 focus:border-brand-teal focus:outline-none focus:ring-2 focus:ring-brand-teal/30"
            />
          </div>

          {error && <p className="text-sm font-medium text-rose-600">{error}</p>}

          <div className="flex justify-end gap-3 pt-2">
            <Button type="button" variant="secondary" onClick={onClose} disabled={submitting}>
              Cancel
            </Button>
            <Button type="submit" variant="primary" loading={submitting} disabled={!valid || submitting}>
              Submit grade
            </Button>
          </div>
        </form>
      </Card>
    </div>
  );
}
